import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, getImagesUrl, failedResponse } from "../common";
import { StudentInfo } from "./studentSlice";
import { getToken } from "../../features/user/UserAvatar";

export interface StudentInsUpdState {
  state: LoadStateInfo;
  student: StudentInfo;
}

const initialState: StudentInsUpdState = {
  state: notStarted(),
  student: emptyStudent(),
};

function emptyStudent() {
  return {
    id: "",
    schoolId: "",
    name: "",
    url: `${getImagesUrl()}/default/student.webp`,
  };
}

export interface StudentInsertRequest {
  student: StudentInfo;
  classroomId?: string;
}
export const studentInsert = createAsyncThunk(
  "insertStudent",
  async (param: StudentInsertRequest, thunkAPI) => {
    console.debug("insert student");

    let query = "";
    if(param.classroomId) {
      query = `?classroomId=${param.classroomId}`;
    }

    const response = await fetch(`${getBackendUrl()}/api/student${query}`, {
      method: "POST",
      signal: thunkAPI.signal,
      headers: {
        "Authorization": `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(param.student)
    });

    if(response.ok) {
      return await response.json();
    } else {
      return thunkAPI.rejectWithValue(failedResponse( response ));
    }
  }
);

export interface StudentUpdateRequest {
  studentId: string;
  student: StudentInfo;
}
export const studentUpdate = createAsyncThunk(
  "updateStudent",
  async (param: StudentUpdateRequest, thunkAPI) => {
    console.debug("update classroom");

    const response = await fetch(`${getBackendUrl()}/api/student/${param.studentId}`, {
      method: "PUT",
      signal: thunkAPI.signal,
      headers: {
        "Authorization": `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(param.student)
    });

    if(response.ok) {
      return await response.json();
    } else {
      return thunkAPI.rejectWithValue(failedResponse( response ));
    }
  }
);

export interface StudentDeleteRequest {
  studentId: string;
}

export const studentDelete = createAsyncThunk(
  "deleteStudent",
  async (param: StudentDeleteRequest, thunkAPI) => {
    console.debug("delete student");
    const response = await fetch(`${getBackendUrl()}/api/student/${param.studentId}`, {
      method: "DELETE",
      signal: thunkAPI.signal,
      headers: {
        "Authorization": `Bearer ${getToken()}`,
      },
    });
    if(response.ok) {
      return param.studentId;
    } else {
      return thunkAPI.rejectWithValue(failedResponse( response ));
    }
  }
);

export const studentInsUpdSlice = createSlice({
  name: "studentInsUpd",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    set: (state, action: PayloadAction<StudentInfo>) => {
      state.state = notStarted();
      state.student = action.payload;
    },
    clean: (state) => {
      state.state = notStarted();
      state.student = emptyStudent();
    }
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
      builder
        .addCase(studentInsert.pending, (state, /* action */) => {
          state.state =  loading();
        })
        .addCase(studentInsert.fulfilled, (state, action) => {
          console.debug(`${action.type}`);
          state.state =  loaded();
          state.student = action.payload;
        })
        .addCase(studentInsert.rejected, (state, action) => {
          state.state = error(action);
        })
        .addCase(studentUpdate.pending, (state, /* action */) => {
          state.state =  loading();
        })
        .addCase(studentUpdate.fulfilled, (state, action) => {
          console.debug(`${action.type}`);
          state.state =  loaded();
          state.student = action.payload;
        })
        .addCase(studentUpdate.rejected, (state, action) => {
          state.state = error(action);
        })
        .addCase(studentDelete.pending, (state, /* action */) => {
          state.state = loading();
        })
        .addCase(studentDelete.fulfilled, (state, /* action */) => {
          state.state = loaded();
          state.student = emptyStudent();
        })
        .addCase(studentDelete.rejected, (state, action) => {
          state.state = error(action);
        });
    },
});

// export const { } = studentInsUpdSlice.actions;

export const selectStudentInsUpd = (state: RootState) => state.studentInsUpd;

export default studentInsUpdSlice.reducer;
